import React, { useState, useEffect, useRef } from 'react';
import { trackUserEvent } from '@/utils/userTracking';
import { useNavigate, useParams } from 'react-router-dom';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { apiClient } from '@rti/shared';

interface MarketIntelProps {}

const MarketIntel: React.FC<MarketIntelProps> = () => {
  const navigate = useNavigate();
  const { reportID } = useParams<{ reportID: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [reportConfig, setReportConfig] = useState<any>(null);
  const [reportName, setReportName] = useState<string | null>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const trackingFiredRef = useRef(false);

  useEffect(() => {
    const loadReportData = async () => {
      if (!reportID) return;

      try {
        setIsLoading(true);
        setError(null);

        // Helper to fetch report options and gracefully handle nulls
        const fetchReportOptions = async (): Promise<any | null> => {
          try {
            const ro = await apiClient.getReportOptions(reportID);

            if (!ro || !ro.options || !ro.options.embed_token || !ro.options.embed_token.token) {
              return null;
            }
            return ro;
          } catch (e) {
            console.error('Error fetching report options:', e);
            return null;
          }
        };


        let reportOptions = await fetchReportOptions();

        if (!reportOptions) {
          throw new Error('Unable to retrieve this report. Please try again.');
        }

        const accessToken = reportOptions.options.embed_token.token as string;

        // The report details are in the options object
        const reportData = reportOptions.options;

        setReportName(reportOptions?.options?.name || null);

        // Configure PowerBI report using the data from options
        setReportConfig({
          type: 'report', // PowerBI report type
          tokenType: models.TokenType.Embed,
          accessToken: accessToken,
          embedUrl: reportData.embedUrl,
          id: reportData.id,
          permissions: models.Permissions.All,
          viewMode: models.ViewMode.View,
          settings: {
            background: models.BackgroundType.Transparent,
            panes: {
              filters: {
                expanded: false,
                visible: false
              },
              pageNavigation: {
                visible: true
              }
            },
            bars: {
              statusBar: {
                visible: true
              }
            }
          }
        });

        // (Tracking now handled in useEffect below)

        // Create user session using report ID
        await apiClient.createUserSession({ report_id: parseInt(reportID) } as any);

        setIsLoading(false);
      } catch (error: any) {
        console.error('Failed to load report:', error);
        setError(error?.message || 'Failed to load report');
        setIsLoading(false);
      }
    };

    loadReportData();

    // Clean up function - close user session when component unmounts
    return () => {
      const closeSession = async () => {
        try {
          await apiClient.closeUserSession();
        } catch (error) {
          console.error('Failed to close session:', error);
        }
      };

      closeSession();
    };
  }, [reportID, reloadKey]);

  // Handle window resize to adjust dashboard height
  useEffect(() => {
    const handleResize = () => {
      if (dashboardRef.current) {
        // Use viewport height instead of parent element height
        const viewportHeight = window.innerHeight;
        // Subtract the toolbar height to avoid scrollbars (approximately 65px with padding)
        const adjustedHeight = viewportHeight - 65;
        dashboardRef.current.style.height = `${adjustedHeight}px`;
      }
    };

    window.addEventListener('resize', handleResize);
    // Call once to set initial size
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track dashboard view event once per load, even under React Strict Mode
  useEffect(() => {
    if (!reportName || trackingFiredRef.current) return;
    trackUserEvent('powerbi_dashboard_viewed', reportName);
    trackingFiredRef.current = true;
  }, [reportName]);

  const handleBackClick = async () => {
    try {
      await apiClient.closeUserSession();
      navigate(`/dashboard`);
    } catch (error) {
      console.error('Failed to close session:', error);
      navigate(`/dashboard`);
    }
  };

  const handleRetry = () => setReloadKey((k) => k + 1);

  return (
    <div
      id="dashboard"
      className={`h-full w-full`}
    >
      <div id="content" className="w-full h-full">
        {isLoading ? (
          <div className="text-3xl flex text-rs-off-white items-center justify-center min-h-[90vh] w-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        ) : error ? (
          <div className="text-rs-off-white min-h-[90vh] w-full flex flex-col items-center justify-center gap-4 px-4 text-center">
            <div className="text-xl">{error}</div>
            <div className="flex gap-3">
              <button className="py-3 px-6 jet" onClick={handleRetry}>
                <i className="material-symbols-outlined mr-2">refresh</i>
                <span>Retry</span>
              </button>
              <button className="py-3 px-6 jet" onClick={handleBackClick}>
                <i className="material-symbols-outlined mr-2">arrow_back</i>
                <span>Back to Landing Page</span>
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="content-toolbar px-4 py-4 flex items-center justify-between">
              <button
                className="py-5 px-8 jet "
                onClick={handleBackClick}
              >
                <i className="material-symbols-outlined mr-2" >arrow_back</i>
                <span>Back to Landing Page</span>
              </button>
              {reportName && (
                <h2 className="text-rs-off-white text-xl font-light">
                  {reportName.replace(' Dashboard', '').replace('RTi', '')} Market Intel
                </h2>
              )}
            </div>
            <div
              ref={dashboardRef}
              className="content-dashboard-container bg-rs-black w-full h-full"
            >
              {reportConfig && (
                <PowerBIEmbed
                  embedConfig={reportConfig}
                  cssClassName="h-full w-full min-h-[90vh]"
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MarketIntel;